import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Report, ReportField } from '../../../models/api/reports/report';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import { ExportService } from '../../../services/export/export.service';
import { ReportsService } from '../../../services/reports/reports.service';
import { ReportFieldExportMapper } from '../../../utilities/report-field-export-mapper';
import { BaseComponent } from '../../base/base.component';
import {
  ExportButtonComponent,
  ExportFormat,
} from '../../core/buttons/export-button.component';
import { FilterInputComponent } from '../../core/controls/filter-input.component';
import { RadioButtonFieldComponent } from '../../core/fields/radio-button-field.component';
import { DialogComponent } from '../../core/panels/dialog.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { TreeComponent, TreeNode } from '../../core/tree/tree.component';
import { dialogPaginatorDropdownStyles } from '../../core/table/dialog-paginator-dropdown.styles';

@Component({
  selector: 'app-report-field-dialog',
  host: {
    class: 'dialog-table-paginator-dropdown-self',
  },
  imports: [
    TableComponent,
    ColumnComponent,
    DialogComponent,
    FilterInputComponent,
    HighlightPipe,
    ExportButtonComponent,
    EmptyStateComponent,
    RadioButtonFieldComponent,
    TreeComponent,
  ],
  template: `
    <app-dialog
      #dialog
      label="Select a report field"
      width="70rem"
      (show)="dialogShow()"
    >
      <ng-template #content>
        <div class="tw-h-[660px]">
          <!-- Search Mode Toggle -->
          <div class="tw-pb-4">
            <app-radio-button-field
              [control]="searchModeControl"
              label=""
              optionLabel="name"
              optionValue="code"
              [options]="searchModeOptions"
              [vertical]="false"
            />
          </div>

          <!-- Search by Name Mode -->
          @if (searchMode() === 'name') {
            <div class="tw-flex tw-items-center tw-justify-between tw-pb-6">
              <app-filter-input
                #filterInput
                [control]="search"
                label="Search by name"
                recordLabel="field"
                [recordCount]="fields.length"
                [asyncSearch]="true"
                [searchPending]="loading"
                classes="tw-w-[400px]"
              />
              <app-export-button (export)="onExport($event)" />
            </div>

            <div>
              @if (!hasSearched) {
                <app-empty-state
                  icon="fa-light fa-table"
                  label="Search results"
                  message="Search by field name"
                  height="500px"
                />
              } @else {
                <app-table
                  [dataSource]="fields"
                  recordLabel="field"
                  scrollHeight="400px"
                  paginatorDropdownAppendTo="self"
                  [showFilter]="false"
                  [showFilterButton]="false"
                  [showToolbar]="false"
                  [loading]="loading || (fields.length > 0 && !resultsReady)"
                  [loadingHeight]="500"
                  [error]="searchError"
                  emptyMessage="Try searching by a different field name"
                  [ariaTableLabel]="'Report field selection'"
                  sortField="defaultValue"
                  [sortOrder]="1"
                >
                  <app-column
                    name="DisplayName"
                    label="Field"
                    sortName="defaultValue"
                  >
                    <ng-template let-data>
                      <a
                        href="#"
                        [attr.aria-label]="'Select ' + data.defaultValue"
                        (click)="$event.preventDefault(); select(data)"
                        [innerHTML]="data.defaultValue | highlight: filterValue"
                      ></a>
                    </ng-template>
                  </app-column>
                  <app-column
                    name="Description"
                    label="Description"
                    sortName="description"
                  >
                    <ng-template let-data>
                      <span
                        class="tw-text-wrap"
                        [innerHTML]="data.description | highlight: filterValue"
                      ></span>
                    </ng-template>
                  </app-column>
                </app-table>
              }
            </div>
          }

          <!-- Search by Category Mode -->
          @if (searchMode() === 'category') {
            <div class="tw-flex tw-justify-end tw-pb-4">
              <app-export-button (export)="onExport($event)" />
            </div>
            @if (categoryLoading()) {
              <div
                class="tw-h-[540px] tw-flex tw-items-center tw-justify-center"
              >
                <i class="fa-solid fa-spinner fa-spin tw-text-2xl"></i>
              </div>
            } @else {
              <div
                class="tw-h-[540px] tw-overflow-auto tw-border tw-rounded-lg"
              >
                <app-tree
                  [dataSource]="categoryNodes()"
                  mode="single"
                  (select)="onCategorySelect($event)"
                  (expand)="loadCategoryChildren($event)"
                />
              </div>
            }
          }
        </div>
      </ng-template>
    </app-dialog>
  `,
  styles: dialogPaginatorDropdownStyles,
})
export class ReportFieldDialogComponent
  extends BaseComponent
  implements OnInit
{
  private cdr = inject(ChangeDetectorRef);
  // private el = inject(ElementRef);
  private reports = inject(ReportsService);
  private exportService = inject(ExportService);

  @ViewChild('dialog')
  dialog: DialogComponent;

  @ViewChild('filterInput')
  filterInput?: FilterInputComponent;

  @ViewChild('filterInput', { read: ElementRef })
  filterInputEl: ElementRef;

  @Output()
  selected = new EventEmitter<ReportField>();

  // Search mode
  searchMode = signal<'name' | 'category'>('name');
  searchModeControl = new FormControl<string>('name');
  readonly searchModeOptions = [
    { name: 'Search by name', code: 'name' },
    { name: 'Search by category', code: 'category' },
  ];

  search = new FormControl<string>(null);

  fields: ReportField[] = [];

  filterValue: string;

  loading = false;

  hasSearched = false;

  searchError: unknown = null;

  /** Defers table rows until after the screen-reader success announce. */
  resultsReady = true;

  // Category tree state
  categoryNodes = signal<TreeNode[]>([]);
  categoryLoading = signal<boolean>(false);

  // Parameters for filtering field categories (used by DYO comparative reports)
  private currentReportId: string | null = null;
  private currentIpColumn: number | null = null;
  private currentIpBreakId: number | null = null;
  private excludeFieldKeys = new Set<string>();
  private searchGeneration = 0;

  ngOnInit(): void {
    this.setupFormListeners();
  }

  private setupFormListeners(): void {
    // Search mode toggle listener
    this.dispose.subs = this.searchModeControl.valueChanges.subscribe(
      (value) => {
        this.searchMode.set(value as 'name' | 'category');
        // Clear search results when switching modes
        this.fields = [];
        this.search.setValue(null);
        this.hasSearched = false;
        this.searchError = null;

        // Load root categories when switching to category mode
        if (value === 'category' && this.categoryNodes().length === 0) {
          this.loadRootCategories();
        }

        this.cdr.markForCheck();
      },
    );

    // Search by name listener
    this.dispose.subs = this.search.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((value) => this.searchFields(value));
  }

  async searchFields(searchTerm: string) {
    if (!searchTerm || searchTerm.trim().length === 0) {
      this.searchGeneration++;
      this.fields = [];
      this.filterValue = '';
      this.hasSearched = false;
      this.loading = false;
      this.resultsReady = true;
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.resultsReady = false;
    this.searchError = null;
    this.filterValue = searchTerm;
    this.hasSearched = true;
    this.cdr.markForCheck();

    const generation = ++this.searchGeneration;
    const trimmedTerm = searchTerm.trim();

    try {
      const report = this.reports.currentReport();
      const results = await this.api.reports.searchReportFields({
        system: report.reportInfo.systemId,
        model: report.reportTemplate.model,
        reportId: this.currentReportId,
        contractNumber: report.reportInfo.contractNumber,
        searchTerm: searchTerm,
        categoryNodeId: null,
        ipColumn: this.currentIpColumn,
        ipBreakId: this.currentIpBreakId,
        isDyo: report.reportTemplate.title?.toLowerCase().includes('design'),
      });
      const nextFields =
        this.excludeFieldKeys.size > 0
          ? results.filter(
              (f) =>
                !this.excludeFieldKeys.has(`${f.tableAlias}.${f.columnName}`),
            )
          : results;

      if (generation !== this.searchGeneration) {
        return;
      }
      if (this.search.value?.trim() !== trimmedTerm) {
        return;
      }

      this.fields = nextFields;
    } catch {
      if (generation !== this.searchGeneration) {
        return;
      }
      if (this.search.value?.trim() !== trimmedTerm) {
        return;
      }
      this.fields = [];
    } finally {
      if (generation === this.searchGeneration) {
        this.loading = false;
        this.resultsReady = true;
        this.cdr.markForCheck();
      }
    }
  }

  show(
    reportId?: string | null,
    ipColumn?: number | null,
    ipBreakId?: number | null,
    excludeFieldKeys?: string[],
  ) {
    this.currentReportId = reportId ?? null;
    this.currentIpColumn = ipColumn ?? null;
    this.currentIpBreakId = ipBreakId ?? null;
    this.excludeFieldKeys = new Set(excludeFieldKeys ?? []);
    this.searchModeControl.setValue('name', { emitEvent: false });
    this.searchMode.set('name');
    this.search.setValue(null);
    this.fields = [];
    this.hasSearched = false;
    this.loading = false;
    this.resultsReady = true;
    this.searchGeneration = 0;
    this.categoryNodes.set([]);
    this.dialog.visible = true;
  }

  close() {
    this.dialog.visible = false;
  }

  select(field: ReportField) {
    this.selected.emit(field);
    this.close();
  }

  dialogShow() {
    setTimeout(() => {
      const input = this.filterInputEl?.nativeElement?.querySelector(
        '.p-inputtext',
      ) as HTMLElement | null;
      input?.focus();
    }, 100);
  }

  // private removeScrollContainerTabindex(): void {
  //   const container = this.el.nativeElement.querySelector(
  //     '.p-datatable-table-container',
  //   );
  //   container?.removeAttribute('tabindex');
  // }

  // Category tree methods
  async loadRootCategories(): Promise<void> {
    this.categoryLoading.set(true);

    try {
      const report = this.reports.currentReport();
      const fields = await this.api.reports.searchReportFields({
        system: report.reportInfo.systemId,
        model: report.reportTemplate.model,
        reportId: this.currentReportId,
        contractNumber: report.reportInfo.contractNumber,
        categoryNodeId: 0,
        ipColumn: this.currentIpColumn,
        ipBreakId: this.currentIpBreakId,
        isDyo: report.reportTemplate.title?.toLowerCase().includes('design'),
      });

      this.categoryNodes.set(this.mapToTreeNodes(fields));
    } finally {
      this.categoryLoading.set(false);
      this.cdr.markForCheck();
    }
  }

  async loadCategoryChildren(node: TreeNode): Promise<void> {
    const report = this.reports.currentReport();
    const fields = await this.api.reports.searchReportFields({
      system: report.reportInfo.systemId,
      model: report.reportTemplate.model,
      reportId: this.currentReportId,
      contractNumber: report.reportInfo.contractNumber,
      categoryNodeId: node.value,
      ipColumn: this.currentIpColumn,
      ipBreakId: this.currentIpBreakId,
      isDyo: report.reportTemplate.title?.toLowerCase().includes('design'),
    });

    node.children = this.mapToTreeNodes(fields);
    node.loaded = true;
    node.loading = false;

    this.categoryNodes.set([...this.categoryNodes()]);
  }

  onCategorySelect(nodeOrNodes: TreeNode | TreeNode[]): void {
    const node = Array.isArray(nodeOrNodes) ? nodeOrNodes[0] : nodeOrNodes;
    if (!node) return;

    if (node.leaf && node.selectable !== false && node.data) {
      this.selected.emit(node.data as ReportField);
      this.close();
    }
  }

  private mapToTreeNodes(fields: ReportField[]): TreeNode[] {
    const sortedFields = [...fields].sort((a, b) => {
      const labelA = (a.defaultValue || '').toLowerCase();
      const labelB = (b.defaultValue || '').toLowerCase();
      return labelA.localeCompare(labelB);
    });

    return sortedFields.map((f) => {
      const node = new TreeNode();
      const isLeaf = f.isLeaf === true;

      node.label = f.defaultValue;
      node.value = isLeaf
        ? `${f.tableAlias}.${f.columnName}`
        : f.categoryNodeId;
      node.leaf = isLeaf;
      node.loaded = false;
      node.children = [];
      node.data = f;
      node.selectable =
        isLeaf && !this.excludeFieldKeys.has(`${f.tableAlias}.${f.columnName}`);
      node.tooltip = f.description || '';

      return node;
    });
  }

  async onExport(format: ExportFormat): Promise<void> {
    const report = this.reports.currentReport();
    const allFields = await this.fetchAllReportFields(report);

    const leafFields = allFields
      .filter((f) => f.isLeaf && f.defaultValue)
      .sort((a, b) => a.defaultValue.localeCompare(b.defaultValue));

    const reportName = report.reportTemplate.title;
    const clientName =
      report.reportInfo.clientName || report.reportInfo.contractNumber;

    if (format === 'xlsx') {
      const excelData = ReportFieldExportMapper.toExcelExport(
        leafFields,
        reportName,
        clientName,
      );
      await this.exportService.exportExcel(excelData, excelData.fileName);
    } else {
      const csvData = ReportFieldExportMapper.toCsvExport(
        leafFields,
        reportName,
        clientName,
      );
      await this.exportService.exportCsv(csvData, csvData.fileName);
    }
  }

  private async fetchAllReportFields(report: Report): Promise<ReportField[]> {
    const allFields: ReportField[] = [];
    const visited = new Set<number>();

    const fetchNode = async (nodeId: number) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);

      const fields = await this.api.reports.searchReportFields({
        system: report.reportInfo.systemId,
        model: report.reportTemplate.model,
        reportId: this.currentReportId,
        contractNumber: report.reportInfo.contractNumber,
        categoryNodeId: nodeId,
        ipColumn: this.currentIpColumn,
        ipBreakId: this.currentIpBreakId,
        isDyo: report.reportTemplate.title?.toLowerCase().includes('design'),
      });

      for (const field of fields) {
        allFields.push(field);
        // If it's a category (not a leaf), recursively fetch its children
        if (!field.isLeaf && field.categoryNodeId) {
          await fetchNode(field.categoryNodeId);
        }
      }
    };

    await fetchNode(0); // Start from root
    return allFields;
  }
}
